import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  computed,
  input,
  signal,
} from "@angular/core";
import { PublishIssue } from "../../../services/publish/types";
import {
  IssueRef,
  TabReadinessSummary,
} from "../../../services/publish/tab-readiness.types";

/**
 * Persistent banner shown at the top of the active tab's editor content.
 * Lists every blocker and warning for THAT tab so the user can fix
 * issues without leaving the tab.
 *
 * Hidden entirely when the summary is empty or all-clean (no need to
 * shout "everything is fine"). Click-an-issue routes through the
 * shell's existing `PublishDrawerController.navigateToIssue` plumbing.
 */
@Component({
  selector: "app-tab-readiness-banner",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./tab-readiness-banner.html",
  styleUrls: ["./tab-readiness-banner.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabReadinessBanner {
  readonly summary = input<TabReadinessSummary | null | undefined>(null);
  readonly tabLabel = input<string>("");

  @Output() issueSelect = new EventEmitter<IssueRef>();

  /** The issue lists start collapsed (just the header + counts) — with many
   *  items they'd otherwise push the editor far down. Click the header to open. */
  protected readonly expanded = signal(false);
  protected toggle(): void {
    this.expanded.update((v) => !v);
  }

  protected readonly blockers = computed(() => {
    const s = this.summary();
    return s ? s.issues.filter((i) => i.severity === "error") : [];
  });
  protected readonly warnings = computed(() => {
    const s = this.summary();
    return s ? s.issues.filter((i) => i.severity === "warning") : [];
  });
  /** Hide the banner entirely when there's nothing to surface. */
  protected readonly hasIssues = computed(
    () => this.blockers().length > 0 || this.warnings().length > 0,
  );

  /** Most severe tier present — drives the banner's left-border accent color. */
  protected readonly tier = computed<"error" | "warning" | null>(() => {
    if (this.blockers().length > 0) return "error";
    if (this.warnings().length > 0) return "warning";
    return null;
  });

  protected issueKey(issue: PublishIssue): string {
    return [
      issue.tabId ?? "",
      issue.sectionId ?? "",
      issue.fieldKey ?? "",
      issue.message,
    ].join("|");
  }

  protected onIssueClick(issue: PublishIssue): void {
    this.issueSelect.emit({
      tabId: issue.tabId ?? this.summary()?.tabId ?? "",
      sectionId: issue.sectionId,
      fieldKey: issue.fieldKey,
    });
  }
}
